import { styled } from '@mui/material/styles'
import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import Constants from 'Constants'

const { PUBLIC_ASSETS_BUCKET_NAME, STORAGE_PATH, MEDIA_SERVER } = Constants

const BASE_PATH = `${STORAGE_PATH}${PUBLIC_ASSETS_BUCKET_NAME}/`
const DEFAULT_LOGO_PATH = `${MEDIA_SERVER}HeaderLogo.png`

interface LogoCacheEntry {
  path: string
  timestamp: number
}

const logoCache: Record<string, LogoCacheEntry> = {
  default: { path: DEFAULT_LOGO_PATH, timestamp: Date.now() },
}

const CACHE_EXPIRATION = 24 * 60 * 60 * 1000

const logoCacheState = { version: 0 }

export function invalidateLogoCache(businessId: string) {
  const cacheKey = `business_${businessId}`
  delete logoCache[cacheKey]
  logoCacheState.version += 1
  window.dispatchEvent(new CustomEvent('hh-logo-cache-invalidated'))
}

export function getLogoCacheVersion() {
  return logoCacheState.version
}

const Logo = styled('img')`
  height: 32px;
  margin-right: 16px;
`

const validateImageAsync = (url: string): Promise<boolean> => {
  return new Promise(resolve => {
    const img = new Image()
    img.src = url

    if (img.complete) {
      resolve(true)
      return
    }

    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
  })
}

const BusinessLogo = () => {
  const businessId = useSelector(
    (state: Record<string, Record<string, Record<string, Record<string, unknown>>>>) =>
      state?.AuthReducer?.userInfo?.businessInfo?.businessId as string | undefined,
    shallowEqual
  )
  const [logoPath, setLogoPath] = useState(DEFAULT_LOGO_PATH)
  const loadedBusinessIdRef = useRef<string | null>(null)
  const [refreshCount, setRefreshCount] = useState(0)

  useEffect(() => {
    const handler = () => {
      loadedBusinessIdRef.current = null
      setRefreshCount(n => n + 1)
    }
    window.addEventListener('hh-logo-cache-invalidated', handler)
    return () => window.removeEventListener('hh-logo-cache-invalidated', handler)
  }, [])

  useEffect(() => {
    if (businessId === loadedBusinessIdRef.current) {
      return
    }

    if (!businessId) {
      setLogoPath(DEFAULT_LOGO_PATH)
      loadedBusinessIdRef.current = businessId ?? null
      return
    }

    const cacheKey = `business_${businessId}`
    const now = Date.now()
    const cachedLogo = logoCache[cacheKey]

    if (cachedLogo && now - cachedLogo.timestamp < CACHE_EXPIRATION) {
      setLogoPath(cachedLogo.path)
      loadedBusinessIdRef.current = businessId
      return
    }

    const cacheBust = `?v=${Date.now()}`
    const clientLogo = `${BASE_PATH}${businessId}/business_logo.png${cacheBust}`

    const validateAndUpdateLogo = async () => {
      const exists = await validateImageAsync(clientLogo)
      const finalLogoPath = exists ? clientLogo : DEFAULT_LOGO_PATH

      logoCache[cacheKey] = {
        path: finalLogoPath,
        timestamp: Date.now(),
      }

      setLogoPath(finalLogoPath)
      loadedBusinessIdRef.current = businessId
    }

    validateAndUpdateLogo()
  }, [businessId, refreshCount])

  const MemoizedLogo = useMemo(() => {
    return <Logo alt="" src={logoPath} />
  }, [logoPath])

  return MemoizedLogo
}

export default memo(BusinessLogo)
